"use client";

import { FaAngleDown, PiList } from "@/components/icons";
import { useCurrency } from "@/contexts/CurrencyContext";
// import SettingsIcon from "@/assets/icons/tsx/SettingsIcon";
import Link from "next/link";
import {
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { useNavMenu } from "@/contexts/NavMenuContext";
import { useLiveData } from "@/contexts/LiveDataContext";
import { useCharts } from "@/contexts/ChartsContext";
import { useConverters } from "@/contexts/ConvertersContext";
import { chartNavDropdownItems, isNavDropdownItemActive, navItems, toolsNavDropdownItems } from "@/constants/nav";
import type { DropdownItem, MenuItem } from "@/constants/nav";
import dynamic from "next/dynamic";

const HeaderPriceBadge = dynamic(() => import("./HeaderPriceBadge"), {
  ssr: false,
  loading: () => (
    <span
      className="bg-accent-gold/28 ml-2 rounded-[3px] inline-block min-h-[19px] min-w-[48px]"
      aria-hidden
    />
  ),
});

interface HeaderProps {
  /** Server-rendered logo (pass `<HeaderLogo />` from layout for early LCP). */
  logo: ReactNode;
  /** Server-fetched chart links for the Charts dropdown (avoids empty menu if context hydrates late). */
  chartDropdownItems?: DropdownItem[];
}

const Header = ({
  logo,
  chartDropdownItems: chartDropdownItemsProp,
}: HeaderProps): React.ReactElement => {
  const pathname = usePathname();
  const [hoveredDropdown, setHoveredDropdown] = useState<number | null>(null);
  const [hoveredSubDropdown, setHoveredSubDropdown] = useState<number | null>(null);
  const { open: openNavMenu } = useNavMenu();
  const { liveData } = useLiveData();
  const { charts } = useCharts();
  const { converters } = useConverters();
  const { convertUsd, currencySymbol } = useCurrency();
  // Build dropdown items from Strapi data
  const chartDropdownItems: DropdownItem[] = useMemo(() => {
    if (chartDropdownItemsProp && chartDropdownItemsProp.length > 0) return chartDropdownItemsProp;
    return chartNavDropdownItems(charts);
  }, [chartDropdownItemsProp, charts]);

  const converterDropdownItems: DropdownItem[] = useMemo(() => converters.map((c) => ({
    id: c.id,
    label: c.title,
    href: `/convert/${c.slug}`,
  })), [converters]);

  const livePrice = liveData ? `${currencySymbol}${convertUsd(liveData.usd_price).toFixed(2)}` : undefined;

  const resolvedNavItems = useMemo(() => navItems.map((item) => {
    if (item.id === 0) {
      return { ...item, price: livePrice ?? item.price };
    }
    if (item.dropdownKey === "charts")
      return { ...item, dropdownItems: chartDropdownItems };
    if (item.dropdownKey === "tools")
      return { ...item, dropdownItems: toolsNavDropdownItems(converterDropdownItems) };
    return item;
  }), [livePrice, chartDropdownItems, converterDropdownItems]);

  const isActive = useCallback((
    href: string | undefined,
    hasDropdown?: boolean,
    dropdownItems?: DropdownItem[],
  ) => {
    if (!href) {
      // For dropdown-only items without href, check if any dropdown item is active
      if (hasDropdown && dropdownItems) {
        return dropdownItems.some((item) => isNavDropdownItemActive(pathname, item));
      }
      return false;
    }
    if (href === "/" && pathname === "/") return true;
    if (href !== "/" && pathname.startsWith(href)) return true;
    if (hasDropdown && dropdownItems) {
      return dropdownItems.some((item) => isNavDropdownItemActive(pathname, item));
    }
    return false;
  }, [pathname]);

  return (
    <header className="w-full relative z-100">
      <div className="flex items-center justify-between px-4 sm:px-[20px] min-[992px]:px-[30px] py-4 min-[992px]:py-[20px] border-b border-foreground/10">
        {/* Left Section - Logo + Nav */}
        <div className="flex items-center xl:gap-[70px] gap-12 min-[992px]:gap-12">
          {logo}

          {/* Desktop Navigation - visible from 992px */}
          <nav className="hidden min-[992px]:flex items-center xl:gap-8 2xl:gap-10 gap-6">
            {resolvedNavItems.map((item, index) => {
              const active = isActive(
                item.href,
                item.hasDropdown,
                item.dropdownItems,
              );

              return (
                <div
                  key={item.id + item.label + index}
                  className="relative group"
                  onMouseEnter={() =>
                    item.hasDropdown && setHoveredDropdown(item.id)
                  }
                  onMouseLeave={() => {
                    setHoveredDropdown(null);
                    setHoveredSubDropdown(null);
                  }}
                >
                  {item.hasDropdown ? (
                    <>
                      {item.href ? (
                        <Link
                          href={item.href}
                          className={`flex items-center gap-[5px] font-kode-mono text-[14px] font-normal tracking-[-1px] transition-colors py-[10px] ${
                            active
                              ? "text-accent-gold"
                              : "text-foreground hover:text-accent-gold"
                          }`}
                        >
                          {item.label}
                          <FaAngleDown
                            className={`w-[15px] h-[15px] transition-transform duration-200 ${
                              hoveredDropdown === item.id ? "rotate-180" : ""
                            }`}
                          />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className={`flex items-center gap-[5px] font-kode-mono text-[14px] font-normal tracking-[-1px] transition-colors py-[10px] cursor-pointer ${
                            active
                              ? "text-accent-gold"
                              : "text-foreground hover:text-accent-gold"
                          }`}
                        >
                          {item.label}
                          <FaAngleDown
                            className={`w-[15px] h-[15px] transition-transform duration-200 ${
                              hoveredDropdown === item.id ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      )}

                      {/* Dropdown Menu - Shows on hover */}
                      <div
                        className={`absolute top-full left-[-10px] z-50 pt-0 transition-all duration-150 ${
                          hoveredDropdown === item.id
                            ? "opacity-100 visible translate-y-0"
                            : "opacity-0 invisible -translate-y-2"
                        }`}
                      >
                        <div className="min-w-[220px] border border-foreground/7 bg-foreground/4 backdrop-blur-[6px] shadow-xl relative overflow-visible">
                          <div>
                            {item.dropdownItems?.map((dropdownItem) => {
                              const dropdownActive = isNavDropdownItemActive(pathname, dropdownItem);

                              if (dropdownItem.subItems) {
                                return (
                                  <div
                                    key={dropdownItem.id}
                                    className="relative"
                                    onMouseEnter={() => setHoveredSubDropdown(dropdownItem.id)}
                                    onMouseLeave={() => setHoveredSubDropdown(null)}
                                  >
                                    <span
                                      className={`flex items-center justify-between gap-2 px-[16px] py-[10px] transition-colors cursor-pointer ${
                                        dropdownActive
                                          ? "text-accent-gold bg-white/5"
                                          : "text-foreground hover:text-accent-gold hover:bg-white/5"
                                      }`}
                                    >
                                      <span className="font-kode-mono text-[13px] font-normal leading-normal">
                                        {dropdownItem.label}
                                      </span>
                                      <FaAngleDown className="w-3 h-3 shrink-0 -rotate-90" />
                                    </span>
                                    <div
                                      className={`absolute left-[219.4px] top-[-1px] pl-0 transition-all duration-150 ${
                                        hoveredSubDropdown === dropdownItem.id
                                          ? "opacity-100 visible translate-x-0"
                                          : "opacity-0 invisible -translate-x-2"
                                      }`}
                                    >
                                      <div className="min-w-[220px] border border-l-0 border-foreground/7 bg-[#1e1f19] backdrop-blur-[6px] shadow-xl overflow-hidden">
                                        {dropdownItem.subItems.map((subItem) => {
                                          const subActive = subItem.href
                                            ? isNavDropdownItemActive(pathname, subItem)
                                            : false;
                                          return (
                                            <Link
                                              key={subItem.id}
                                              href={subItem.href ?? "#"}
                                              className={`flex items-center gap-[12px] px-[16px] py-[10px] transition-colors ${
                                                subActive
                                                  ? "text-accent-gold bg-white/5"
                                                  : "text-foreground hover:text-accent-gold hover:bg-white/5"
                                              }`}
                                            >
                                              <span className="font-kode-mono text-[13px] font-normal leading-normal">
                                                {subItem.label}
                                              </span>
                                            </Link>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <Link
                                  key={dropdownItem.id}
                                  href={dropdownItem.href ?? "#"}
                                  className={`flex items-center gap-[12px] px-[16px] py-[10px] transition-colors ${
                                    dropdownActive
                                      ? "text-accent-gold bg-white/5"
                                      : "text-foreground hover:text-accent-gold hover:bg-white/5"
                                  }`}
                                >
                                  <span className="font-kode-mono text-[13px] font-normal leading-normal">
                                    {dropdownItem.label}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                          <div className="absolute blur-[100px] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full h-full z-[-1] bg-linear-to-t from-accent-gold/10 to-transparent"></div>
                        </div>
                      </div>
                    </>
                  ) : item.href ? (
                    <Link
                      href={item.href}
                      className={`flex items-center font-kode-mono text-[14px] font-normal tracking-[-1px] transition-colors ${
                        active
                          ? "text-accent-gold"
                          : "text-foreground hover:text-accent-gold"
                      }`}
                    >
                      {item.label}
                      {item?.price ? <HeaderPriceBadge price={item.price} /> : null}
                    </Link>
                  ) : (
                    <span
                      className={`flex items-center font-kode-mono text-[14px] font-normal tracking-[-1px] transition-colors ${
                        active
                          ? "text-accent-gold"
                          : "text-foreground hover:text-accent-gold"
                      }`}
                    >
                      {item.label}
                      {item?.price ? <HeaderPriceBadge price={item.price} /> : null}
                    </span>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Right Section - Mobile menu */}
        <div className="flex items-center xl:gap-[22px] gap-3 min-[992px]:gap-5">
          <button
            type="button"
            onClick={openNavMenu}
            aria-label="Open navigation menu"
            className="min-[992px]:hidden flex cursor-pointer items-center justify-center p-2 -m-2 text-foreground hover:text-accent-gold transition-colors"
          >
            <PiList className="w-6 h-6" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
